import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const CreateOrderModal = ({ isOpen, onClose, onCreateOrder }) => {
  const [formData, setFormData] = useState({
    client: '',
    serviceType: '',
    priority: 'Média',
    assignedTechnician: '',
    description: '',
    scheduledDate: '',
    scheduledTime: '',
    estimatedHours: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  const clientOptions = [
    { value: 'abc-corp', label: 'ABC Corporation', description: 'Rua das Flores, 123 - Centro' },
    { value: 'xyz-ltda', label: 'XYZ Ltda', description: 'Av. Paulista, 456 - Bela Vista' },
    { value: 'tech-solutions', label: 'Tech Solutions', description: 'Rua da Tecnologia, 789 - Vila Olímpia' },
    { value: 'global-services', label: 'Global Services', description: 'Alameda Santos, 321 - Jardins' }
  ];

  const serviceTypeOptions = [
    { value: 'Manutenção Preventiva', label: 'Manutenção Preventiva' },
    { value: 'Manutenção Corretiva', label: 'Manutenção Corretiva' },
    { value: 'Instalação', label: 'Instalação' },
    { value: 'Consultoria', label: 'Consultoria' },
    { value: 'Suporte Técnico', label: 'Suporte Técnico' },
    { value: 'Treinamento', label: 'Treinamento' }
  ];

  const priorityOptions = [
    { value: 'Baixa', label: 'Baixa' },
    { value: 'Média', label: 'Média' },
    { value: 'Alta', label: 'Alta' }
  ];

  const technicianOptions = [
    { value: 'joao-silva', label: 'João Silva', description: 'Especialista em Redes' },
    { value: 'maria-santos', label: 'Maria Santos', description: 'Técnica em Sistemas' },
    { value: 'carlos-oliveira', label: 'Carlos Oliveira', description: 'Consultor Senior' },
    { value: 'ana-costa', label: 'Ana Costa', description: 'Especialista em Segurança' }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors?.[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.client) {
      newErrors.client = 'Cliente é obrigatório';
    }
    if (!formData?.serviceType) {
      newErrors.serviceType = 'Tipo de serviço é obrigatório';
    }
    if (!formData?.description?.trim()) {
      newErrors.description = 'Descrição é obrigatória';
    }
    if (!formData?.scheduledDate) {
      newErrors.scheduledDate = 'Data agendada é obrigatória';
    }
    if (!formData?.scheduledTime) {
      newErrors.scheduledTime = 'Horário agendado é obrigatório';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      const newOrder = {
        id: Date.now(),
        number: `2024-${String(Math.floor(Math.random() * 1000))?.padStart(3, '0')}`,
        ...formData,
        status: 'Pendente',
        createdAt: new Date()?.toISOString(),
        timeTracking: {
          totalHours: 0,
          totalMinutes: 0,
          isActive: false
        }
      };

      onCreateOrder(newOrder);
      handleClose();
    } catch (error) {
      console.error('Error creating order:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setFormData({
      client: '',
      serviceType: '',
      priority: 'Média',
      assignedTechnician: '',
      description: '',
      scheduledDate: '',
      scheduledTime: '',
      estimatedHours: ''
    });
    setErrors({});
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[1000] bg-background/80 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-card border border-border rounded-lg shadow-modal w-full max-w-2xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
              <Icon name="Plus" size={20} className="text-primary" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-card-foreground">
                Nova Ordem de Serviço
              </h2>
              <p className="text-sm text-muted-foreground">
                Criar uma nova ordem de serviço para atendimento
              </p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={handleClose}
            disabled={isSubmitting}
          >
            <Icon name="X" size={20} />
          </Button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="overflow-y-auto max-h-[calc(90vh-140px)]">
          <div className="p-6 space-y-6">
            {/* Client and Service Type */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Select
                label="Cliente"
                placeholder="Selecione o cliente"
                options={clientOptions}
                value={formData?.client}
                onChange={(value) => handleInputChange('client', value)}
                error={errors?.client}
                required
                searchable
              />
              <Select
                label="Tipo de Serviço"
                placeholder="Selecione o tipo"
                options={serviceTypeOptions}
                value={formData?.serviceType}
                onChange={(value) => handleInputChange('serviceType', value)}
                error={errors?.serviceType}
                required
              />
            </div>

            {/* Priority and Technician */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Select
                label="Prioridade"
                options={priorityOptions}
                value={formData?.priority}
                onChange={(value) => handleInputChange('priority', value)}
                required
              />
              <Select
                label="Técnico Responsável"
                placeholder="Selecione o técnico"
                options={technicianOptions}
                value={formData?.assignedTechnician}
                onChange={(value) => handleInputChange('assignedTechnician', value)}
                searchable
              />
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-card-foreground mb-2">
                Descrição do Serviço *
              </label>
              <textarea
                className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
                rows={4}
                placeholder="Descreva detalhadamente o serviço a ser realizado..."
                value={formData?.description}
                onChange={(e) => handleInputChange('description', e?.target?.value)}
                disabled={isSubmitting}
              />
              {errors?.description && (
                <p className="text-sm text-error mt-1">{errors?.description}</p>
              )}
            </div>

            {/* Schedule */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Input
                type="date"
                label="Data Agendada"
                value={formData?.scheduledDate}
                onChange={(e) => handleInputChange('scheduledDate', e?.target?.value)}
                error={errors?.scheduledDate}
                required
                disabled={isSubmitting}
              />
              <Input
                type="time"
                label="Horário Agendado"
                value={formData?.scheduledTime}
                onChange={(e) => handleInputChange('scheduledTime', e?.target?.value)}
                error={errors?.scheduledTime}
                required
                disabled={isSubmitting}
              />
              <Input
                type="number"
                label="Horas Estimadas"
                placeholder="Ex: 2.5"
                value={formData?.estimatedHours}
                onChange={(e) => handleInputChange('estimatedHours', e?.target?.value)}
                min="0.5"
                step="0.5"
                disabled={isSubmitting}
              />
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end space-x-3 p-6 border-t border-border bg-muted/30">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={isSubmitting}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              loading={isSubmitting}
              iconName="Plus"
              iconPosition="left"
            >
              {isSubmitting ? 'Criando...' : 'Criar Ordem'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateOrderModal;